import { QuillDeltaToHtmlConverter } from "quill-delta-to-html";
import DOMPurify from "dompurify";
import { Delta, QuillOptions } from "quill";

export class QuillService {
  static config: QuillOptions = {
    modules: {
      toolbar: [
        ["bold", "italic", "strike"],
        [{ header: 1 }, { header: 2 }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: ["#000000", "#ffc165", "#9fa3a9"] }],
        [{ align: ["", "center", "right"] }],
        ["blockquote"],
        ["clean"],
      ],
    },
    theme: "snow",
    formats: [
      "bold",
      "italic",
      "strike",
      "header",
      "list",
      "color",
      "align",
      "blockquote",
    ],
  };

  private static colors: Record<string, string> = {
    "#ffc165": "primary",
    "#9fa3a9": "secondary",
  };

  private static classes: Record<string, string> = {
    "ql-align-center": "text-center",
    "ql-align-right": "text-right",
  };

  static quillDeltaToHtml(delta: Delta): string {
    const converter = new QuillDeltaToHtmlConverter(delta.ops, {});
    return converter.convert();
  }

  static restoreQuillHtml(html: string): string {
    html = this.classToColorStyle(html);
    html = this.restoreQuillClass(html);
    html = this.cleanHTML(html);
    return html;
  }

  private static colorStyleToClass(html: string): string {
    Object.keys(this.colors).forEach((hex) => {
      const className = this.colors[hex];
      const regex = new RegExp(`style="color:${hex}"`, "gi");
      html = html.replace(regex, `class="${className}"`);
    });
    return html;
  }

  private static classToColorStyle(html: string): string {
    Object.keys(this.colors).forEach((hex) => {
      const className = this.colors[hex];
      const regex = new RegExp(`class="${className}"`, "gi");
      html = html.replace(regex, `style="color:${hex}"`);
    });
    return html;
  }

  private static cleanQuillClass(html: string): string {
    Object.keys(this.classes).forEach((key) => {
      html = html.replace(key, this.classes[key]);
    });
    return html;
  }

  private static restoreQuillClass(html: string): string {
    Object.keys(this.classes).forEach((key) => {
      html = html.replace(this.classes[key], key);
    });
    return html;
  }

  static cleanHTML(html: string): string {
    html = this.cleanHTMLSpace(html);

    // return empty if text is empty (replace all spaces and &nbsp; by '')
    if (html.replace(/\s|&nbsp;|\n/g, "") === "<p><br/></p>") return "";

    // replace first <p><br> by <p> and last <br/></p> by </p>
    html = html.replace(/^<p><br[^>]*>/, "<p>").replace(/<br\/><\/p>$/, "</p>");

    html = this.colorStyleToClass(html);
    html = this.cleanQuillClass(html);

    html = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p",
        "strong",
        "blockquote",
        "br",
        "span",
        "em",
        "ul",
        "ol",
        "li",
        "b",
        "s",
        "i",
        "u",
        "h1",
        "h2",
        "h3",
      ],
      ALLOWED_ATTR: ["class"],
      FORBID_TAGS: [],
    });

    html = this.cleanHTMLClass(html);
    html = this.cleanHTMLSpace(html);

    return html;
  }

  private static cleanHTMLSpace(html: string): string {
    html = html.replace(/&nbsp;|&amp;nbsp;/g, " ");
    html = html.replace("&amp;nbsp;", " ");
    return html;
  }

  private static cleanHTMLClass(html: string): string {
    // replace "> " by ">&nbsp;" to avoid remove space before html (bug from DOMParser)
    html = html.replace(/^\s/, "&nbsp;");

    const doc = new DOMParser().parseFromString(html, "text/html");
    const elements = doc.querySelectorAll("*");

    const allowedClasses = [
      "text-center",
      "text-right",
      "primary",
      "secondary",
    ];

    elements.forEach((element) => {
      const classList = element.className.split(" ");
      const filteredClasses = classList.filter((className) =>
        allowedClasses.includes(className)
      );

      if (filteredClasses.length === 0) {
        element.removeAttribute("class");
      } else {
        element.className = filteredClasses.join(" ");
      }
    });

    html = doc.body.innerHTML;

    // restore space before html
    html = html.replace(/^&nbsp;/, " ");

    return html;
  }
}
